import { useRef, useState } from "react";
import { Plus } from "lucide-react";
import { ApiError } from "@/core/api/apiTypes";
import { useUpdateReminderSettings } from "@/features/business/hooks/reminderSettingsQueries";
import { ReminderRuleSchema, type ReminderSettings } from "@/features/business/types/businessTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { Select } from "@/shared/components/shadcn/Select";
import { Switch } from "@/shared/components/shadcn/Switch";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormActions } from "@/shared/components/ui/FormActions";
import { FormField } from "@/shared/components/ui/FormField";
import { Section } from "@/shared/components/ui/Section";
import { ReminderScheduleRow } from "./ReminderScheduleRow";

interface ReminderSettingsSectionProps {
  businessId: string;
  settings: ReminderSettings;
  refreshError?: string;
  onRetry: () => void;
  onReload: () => Promise<ReminderSettings>;
}

export function ReminderSettingsSection({
  businessId, settings, refreshError, onRetry, onReload,
}: ReminderSettingsSectionProps) {
  // Background refreshes must not replace the draft or its concurrency revision.
  const [draft, setDraft] = useState(() => structuredClone(settings));
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState(60);
  const [addError, setAddError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [notice, setNotice] = useState("");
  const [conflict, setConflict] = useState(false);
  const [removalId, setRemovalId] = useState<string | null>(null);
  const [reloadOpen, setReloadOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloadError, setReloadError] = useState("");
  const amountInput = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);
  const update = useUpdateReminderSettings(businessId);
  const busy = update.isPending || reloading;
  const hasConflict = conflict || settings.revision !== draft.revision;

  function clearFeedback() {
    setAddError("");
    setSaveError("");
    setNotice("");
  }

  function addReminder() {
    clearFeedback();
    const value = Number(amount);
    const minutesBefore = value * unit;
    const rule = ReminderRuleSchema.safeParse({
      id: crypto.randomUUID(),
      minutes_before: minutesBefore,
    });
    if (!Number.isInteger(value) || value < 1 || !rule.success) {
      setAddError("Enter a positive whole amount, up to 2,147,483,647 minutes in total.");
      amountInput.current?.focus();
      return;
    }
    if (draft.schedules.some((schedule) => schedule.minutes_before === minutesBefore)) {
      setAddError("A reminder already exists for this time before the appointment.");
      amountInput.current?.focus();
      return;
    }
    setDraft((previous) => ({ ...previous, schedules: [...previous.schedules, rule.data] }));
    setAmount("");
    setNotice("Reminder added to the draft. Save to apply your changes.");
    amountInput.current?.focus();
  }

  async function save() {
    if (submitting.current || hasConflict) return;
    clearFeedback();
    if (amount.trim()) {
      setAddError("Add this reminder to the list, or clear the amount, before saving.");
      amountInput.current?.focus();
      return;
    }
    submitting.current = true;
    try {
      const saved = await update.mutateAsync({
        enabled: draft.enabled,
        schedules: draft.schedules,
        expectedRevision: draft.revision,
      });
      setDraft(structuredClone(saved));
      setNotice("Reminder settings saved.");
    } catch (error) {
      if (error instanceof ApiError && error.Status === 409) {
        setConflict(true);
      } else {
        setSaveError(error instanceof Error ? error.message : "Couldn't save reminder settings. Try again.");
      }
    } finally {
      submitting.current = false;
    }
  }

  async function reload() {
    if (submitting.current) return;
    submitting.current = true;
    setReloading(true);
    setReloadError("");
    try {
      const saved = await onReload();
      setDraft(structuredClone(saved));
      setAmount("");
      setConflict(false);
      clearFeedback();
      setReloadOpen(false);
      setNotice("Saved reminder settings loaded.");
    } catch (error) {
      setReloadError(error instanceof Error ? error.message : "Couldn't reload reminder settings. Try again.");
    } finally {
      submitting.current = false;
      setReloading(false);
    }
  }

  return (
    <Section title="Appointment reminders" card>
      <form
        className="space-y-4"
        noValidate
        onSubmit={(event) => { event.preventDefault(); void save(); }}
      >
        {refreshError && (
          <ErrorState title="Couldn't refresh reminders" message={`${refreshError} Your draft is unchanged.`} onRetry={onRetry} />
        )}
        {hasConflict && (
          <div role="alert" className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Reminder settings changed elsewhere. Your draft has been kept.
              Reload the saved settings before making and saving further changes.
            </p>
            <Button variant="outline" disabled={busy} onClick={() => { setReloadError(""); setReloadOpen(true); }}>
              Reload saved settings
            </Button>
          </div>
        )}
        <FieldGroup disabled={busy} className="rounded-none border-0 bg-transparent p-0 sm:p-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <label htmlFor="reminders-enabled" className="text-sm font-semibold">Send appointment reminders</label>
              <p id="reminders-enabled-description" className="text-xs leading-relaxed text-muted-foreground">
                Reminders are for upcoming confirmed appointments. Turning them off keeps your schedules.
              </p>
            </div>
            <Switch
              id="reminders-enabled"
              aria-describedby="reminders-enabled-description"
              size="lg"
              checked={draft.enabled}
              onCheckedChange={(enabled) => { clearFeedback(); setDraft((previous) => ({ ...previous, enabled })); }}
            />
          </div>
          {!draft.enabled && <p className="text-sm text-muted-foreground">Reminders are off in this draft. You can still configure schedules below.</p>}
          {draft.schedules.length ? (
            <div className="space-y-2">
              {[...draft.schedules].sort((a, b) => b.minutes_before - a.minutes_before).map((rule) => (
                <ReminderScheduleRow key={rule.id} rule={rule} disabled={busy} onRemove={() => setRemovalId(rule.id)} />
              ))}
            </div>
          ) : (
            <EmptyState title="No reminder schedules" message="Add a schedule below. No reminders will be sent without a schedule, even when the switch is on." />
          )}
          <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
            <FormField id="reminder-amount" label="Time before appointment" error={addError}>
              <Input
                ref={amountInput}
                type="number"
                min={1}
                max={Math.floor(2147483647 / unit)}
                step={1}
                value={amount}
                onChange={(event) => { clearFeedback(); setAmount(event.target.value); }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") { event.preventDefault(); addReminder(); }
                }}
              />
            </FormField>
            <FormField id="reminder-unit" label="Unit">
              <Select value={unit} onChange={(event) => { clearFeedback(); setUnit(Number(event.target.value)); }}>
                <option value={1}>Minutes</option>
                <option value={60}>Hours</option>
                <option value={1440}>Days</option>
              </Select>
            </FormField>
          </div>
          <Button variant="quiet" onClick={addReminder}><Plus aria-hidden="true" /> Add reminder</Button>
        </FieldGroup>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Changes apply only after saving. Emails are scheduled relative to the appointment start;
          delivery is not guaranteed at the exact minute. Emails already sent cannot be recalled.
        </p>
        {saveError && <ErrorState title="Couldn't save reminders" message={`${saveError} Your draft has been kept.`} />}
        <p role="status" aria-live="polite" className="text-sm text-primary">{notice}</p>
        <FormActions>
          <Button type="submit" disabled={busy || hasConflict}>{update.isPending ? "Saving..." : "Save reminders"}</Button>
        </FormActions>
      </form>
      <ConfirmationDialog
        open={removalId !== null}
        onOpenChange={(open) => { if (!open) setRemovalId(null); }}
        title="Remove this reminder?"
        description="This removes the schedule from your draft only. Save reminders to apply the change."
        confirmLabel="Remove reminder"
        dismissLabel="Keep reminder"
        destructive
        onConfirm={() => {
          clearFeedback();
          setDraft((previous) => ({ ...previous, schedules: previous.schedules.filter((rule) => rule.id !== removalId) }));
          setRemovalId(null);
          setNotice("Reminder removed from the draft. Save to apply your changes.");
          requestAnimationFrame(() => amountInput.current?.focus());
        }}
      />
      <ConfirmationDialog
        open={reloadOpen}
        onOpenChange={setReloadOpen}
        title="Reload saved reminder settings?"
        description="This replaces your unsaved draft with the latest settings. If loading fails, your draft will be kept."
        confirmLabel="Reload settings"
        dismissLabel="Keep reviewing"
        pending={reloading}
        error={reloadError}
        onConfirm={() => void reload()}
      />
    </Section>
  );
}
